import { Watch } from '@Ravnur-Inc/decorators';
import { ErrorObject } from '@vuelidate/core';
import InputMask from 'inputmask-core';
import { Options, Vue, prop } from 'vue-class-component';

import { $Props } from '../../typings/tsx';
import TextField from '../../ui-kit/text-field/text-field';

import ffpoly from './ff-polyfill';
ffpoly();

const KEYS = {
  BACKSPACE: 8,
  LEFT: 37,
  RIGHT: 39,
  END: 35,
  HOME: 36,
  DELETE: 46,
};

const KEY_TO_ACTION: {
  [key: number]: keyof SubType<MaskedInput, (ev: KeyboardEvent) => void>;
} = {
  [KEYS.BACKSPACE]: 'onBackspacePressed',
  [KEYS.LEFT]: 'onLeftPressed',
  [KEYS.RIGHT]: 'onRightPressed',
  [KEYS.END]: 'onEndPressed',
  [KEYS.HOME]: 'onHomePressed',
  [KEYS.DELETE]: 'onDeletePressed',
};

const FORMATS = {
  a: {
    validate: (char: string) => /^[A-Za-z]$/.test(char),
  },
  A: {
    validate: (char: string) => /^[A-Za-z]$/.test(char),
    transform: (char: string) => char.toUpperCase(),
  },
  '*': {
    validate: (char: string) => /^[\dA-Za-z]$/.test(char),
  },
  '#': {
    validate: (char: string) => /^[\dA-Za-z]$/.test(char),
    transform: (char: string) => char.toUpperCase(),
  },
  '+': {
    validate: () => true,
  },
};

class Props {
  mask!: string;
  value = prop({ default: '' });
  placeholderChar = prop({ default: '_' });
  errors = prop<ErrorObject[]>({ default: [] });
  disabled = prop({ default: false });
  readonly = prop({ default: false });
  label = prop({ default: '' });
  placeholder = prop({ default: '' });
  customFormats = prop({ type: Object, required: false });
  requiredAsterisk = prop({ default: false });
}

type Emits = {
  onInput: (value: string) => void;
  onBlur: (value: string) => void;
  onFocus: () => void;
};

@Options({
  name: 'masked-input',
  emits: ['input', 'blur', 'focus'],
})
export default class MaskedInput extends Vue.with(Props) {
  declare $props: $Props<Props, Emits, 'value'>;

  declare $refs: {
    input: TextField;
  };

  public strValue = '';

  private marginLeft = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private core: any = null;

  public get $input() {
    return this.$refs.input.$refs.field as HTMLInputElement & {
      selectionStart: number;
    };
  }

  private get getValue() {
    if (this.core === null) {
      return '';
    }
    return this.core.getValue();
  }

  public onBackspacePressed() {
    const { start, end } = this.core.selection;
    if (start > this.marginLeft || start !== end) {
      this.core.backspace();
      this.updateToCoreState();
    }
  }

  public onLeftPressed() {
    if (this.$input.selectionStart === this.$input.selectionEnd) {
      this.$input.selectionEnd = this.$input.selectionStart--;
    }

    this.core.selection = {
      start: this.$input.selectionStart,
      end: this.$input.selectionStart,
    };
    this.updateToCoreState();
  }

  public onRightPressed() {
    if (this.$input.selectionStart === this.$input.selectionEnd) {
      this.$input.selectionEnd++;
    }

    this.core.selection = {
      start: this.$input.selectionEnd,
      end: this.$input.selectionEnd,
    };
    this.updateToCoreState();
  }

  public onEndPressed() {
    this.$input.selectionStart = this.$input.selectionEnd = this.$input.value.length;
    this.core.selection = {
      start: this.$input.selectionEnd,
      end: this.$input.selectionEnd,
    };
    this.updateToCoreState();
  }

  public onHomePressed() {
    const { $input } = this;
    $input.selectionStart = $input.selectionEnd = 0;
    this.core.selection = {
      start: $input.selectionStart,
      end: $input.selectionStart,
    };
    this.updateToCoreState();
  }

  public onDeletePressed() {
    if (this.$input.selectionStart === this.$input.selectionEnd) {
      this.core.setValue('');
      this.core.setSelection({ start: 0, end: 0 });
      this.$input.selectionStart = this.core.selection.start;
      this.$input.selectionEnd = this.core.selection.start;
    } else {
      this.core.backspace();
    }
    this.updateToCoreState();
  }

  @Watch('value', { immediate: true })
  public changeValue() {
    const { core, value } = this;
    this.strValue = value;

    if (!core || !value) {
      return;
    }

    core.setValue(value); // For multiple inputs support
    this.$input.value = this.core.getValue(); // RMP-1877: Fixes FF bug
  }

  mounted() {
    this.$nextTick(this.initMask);
  }

  render() {
    return (
      <text-field
        ref="input"
        disabled={this.core === null || this.disabled}
        errors={this.errors}
        label={this.label}
        onblur={this.blur}
        oncopy={this.copy}
        oncut={this.cut}
        onfocus={this.focusin}
        onkeydown={this.keyDown}
        onkeypress={this.keyPress}
        onkeyup={this.keyUp}
        onmouseup={this.mouseUp}
        onpaste={this.paste}
        placeholder={this.placeholder}
        readOnly={this.readonly}
        requiredAsterisk={this.requiredAsterisk}
        // FIXME: Having value and vModel in same time make no sense, but somehow this
        // component has TS errors which keep saying that vModel is required
        // making temporary workaround. Will be fixed later.
        vModel={[this.strValue, 'value']}
        value={this.getValue || this.strValue}
        {...{ oninput: this.textInput }}
      />
    );
  }

  @Watch('mask')
  private initMask() {
    const formatCharacters = this.customFormats ? this.customFormats : FORMATS;

    try {
      this.core = new InputMask({
        pattern: this.mask,
        value: '',
        placeholderChar: this.placeholderChar,
        formatCharacters,
      });
      for (let i = 0; i < this.$input.value.length; ++i) {
        this.core.input(this.$input.value[i]);
      }
      this.core.setSelection({ start: 0, end: 0 });
      if (this.$input.value === '') {
        this.$emit('input', '', '');
      } else {
        this.updateToCoreState();
      }
    } catch (e) {
      this.core = null;
      this.$emit('input', this.$input.value, '');
    }
  }

  private keyDown(e: KeyboardEvent) {
    if (this.core === null) {
      e.preventDefault();
      return;
    }
    this.setNativeSelection();

    const action = KEY_TO_ACTION[e.keyCode];
    if (action && this[action]) {
      this[action]();
      e.preventDefault();
    }
  }

  private keyPress(e: KeyboardEvent) {
    if (e.ctrlKey) {
      return; // Fix FF copy/paste issue
    }
    /*
         IE & FF are not trigger textInput event, so we have to force it
      */

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (window as any).InstallTrigger !== 'undefined') {
      e.preventDefault();
      const ie: any = { ...e, data: e.key, inputType: '' };
      this.textInput(ie);
    }
  }

  // FIXME: Type should be InputEvent but we get unresolved TS errors because of that
  // should be fixed later
  private textInput(e: any) {
    if (e.preventDefault) {
      e.preventDefault();
    }

    this.core.input(e.data);
    this.updateToCoreState();
  }

  private keyUp(/* e */) {
    this.updateToCoreState();
  }

  private cut(e: Event) {
    e.preventDefault();
    if (this.$input.selectionStart !== this.$input.selectionEnd) {
      /* let text = this.$refs.input.value.slice(
            this.$refs.input.selectionStart,
            this.$refs.input.selectionEnd
         ) */
      try {
        document.execCommand('copy');
      } catch (err) {
        //
      }
      this.core.backspace();
      this.updateToCoreState();
    }
  }

  private copy(/* e */) {
    // noop
  }

  private paste(e: Event) {
    e.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const text = (e as any).clipboardData.getData('text');
    for (let i = 0; i < text.length; ++i) {
      this.core.input(text[i]);
    }
    this.updateToCoreState();
  }

  private updateToCoreState() {
    if (this.core === null) {
      return;
    }
    if (this.$input.value !== this.core.getValue()) {
      this.$input.value = this.core.getValue();
      this.$emit('input', this.$input.value, this.core.getRawValue());
    }
    this.$input.selectionStart = this.core.selection.start;
    this.$input.selectionEnd = this.core.selection.end;
  }

  private focusin(/* e */) {
    this.$emit('focus');
  }

  private isEmpty() {
    if (this.core === null) {
      return true;
    }
    return this.core.getValue() === this.core.emptyValue;
  }

  private setNativeSelection() {
    this.core.selection = {
      start: this.$input.selectionStart,
      end: this.$input.selectionEnd,
    };
  }

  private mouseUp(/* e */) {
    if (this.isEmpty() && this.$input.selectionStart === this.$input.selectionEnd) {
      this.core.setSelection({ start: 0, end: 0 });
      this.$input.selectionStart = this.core.selection.start;
      this.$input.selectionEnd = this.core.selection.start;
      this.marginLeft = this.core.selection.start;
      this.updateToCoreState();
    } else {
      this.setNativeSelection();
    }
  }

  private blur() {
    this.$emit('blur', this.core.getValue());
  }
}
